import type {
  GtmInstance,
  GtmTrackEventParams,
  GtmModuleOptions,
  GtmIdContainer,
  GtmQueryParams,
} from "../types";

export default defineNuxtPlugin(async (nuxtApp: any) => {
    const keepFilters = useState<boolean>('plp-keep-filters-enabled', () => false);
  const config = useRuntimeConfig();
  const router = useRouter();
  const gtmConfig = config.public.gtm as GtmModuleOptions;

  // Debug: Log the actual configuration being used
  console.log(
    "[GTM] Loaded configuration:",
    JSON.stringify(gtmConfig, null, 2),
  );

  if (gtmConfig?.enabled === false) {
    console.warn("[GTM] GTM is disabled via enabled: false");
    return nuxtApp.provide("gtm", null);
  }

  if (!gtmConfig?.id) {
    console.warn("[GTM] GTM is disabled: no ID provided");
    return nuxtApp.provide("gtm", null);
  }

  let isEnabled = true;
  let isDebug = Boolean(gtmConfig?.debug);
  const dataLayer: any[] = [];

  if (import.meta.client) {
    window.dataLayer = window.dataLayer || [];
    dataLayer.push(...window.dataLayer);
  }

  const pushToDataLayer = (data: any) => {
    if (import.meta.client && window.dataLayer) {
      window.dataLayer.push(data);
    }
    dataLayer.push(data);

    if (isDebug) {
      console.log("[GTM] Pushed to dataLayer:", data);
    }
  };

  const buildScriptUrl = (id: string, queryParams?: GtmQueryParams): string => {
    const baseUrl =
      gtmConfig.source || "https://www.googletagmanager.com/gtm.js";
    const url = new URL(baseUrl);
    url.searchParams.set("id", id);

    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value) {
          url.searchParams.set(key, value);
        }
      });
    }

    const finalUrl = url.toString();
    if (isDebug) {
      console.log(`[GTM] Building script URL for container ${id}:`, finalUrl);
    }

    return finalUrl;
  };

  const loadScript = (src: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (import.meta.server) {
        resolve();
        return;
      }

      if (isDebug) {
        console.log(`[GTM] Loading script:`, src);
      }

      const script = document.createElement("script");
      script.src = src;

      // Use dynamic nonce from server if available, otherwise fall back to config
      const nonce = (window as any).__CSP_NONCE__ || gtmConfig.nonce;
      if (nonce) {
        script.setAttribute("nonce", nonce);
        if (isDebug) {
          console.log(`[GTM] Applied nonce:`, nonce);
        }
      }

      if (gtmConfig.defer) {
        script.defer = true;
      } else {
        script.async = true;
      }

      if (gtmConfig.compatibility) {
        script.async = true;
        script.defer = true;
      }

      script.onload = () => {
        if (isDebug) {
          console.log(`[GTM] Script loaded successfully:`, src);
        }
        resolve();
      };

      script.onerror = (error) => {
        const errorInfo = {
          src,
          error,
          message: "Failed to load GTM script",
          possibleCauses: [
            "Network connectivity issues",
            "Invalid GTM container ID",
            "CSP blocking the script",
            "GTM preview mode authentication issues",
          ],
        };

        if (isDebug) {
          console.error(`[GTM] Script loading failed:`, errorInfo);
        }

        reject(errorInfo);
      };

      document.head.appendChild(script);
    });
  };

  const initializeGTM = async () => {
    if (!gtmConfig.loadScript || import.meta.server) {
      return;
    }

    // Push the GTM start event BEFORE loading the script
    // This is required by GTM to start processing hits
    if (import.meta.client) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
      });

      if (isDebug) {
        console.log("[GTM] Pushed gtm.start event to dataLayer");
      }
    }

    const containers = Array.isArray(gtmConfig.id)
      ? gtmConfig.id
      : [gtmConfig.id];

    for (const container of containers) {
      let id: string;
      let queryParams: GtmQueryParams | undefined;

      if (typeof container === "string") {
        id = container;
        queryParams = gtmConfig.queryParams;
      } else {
        id = (container as GtmIdContainer).id;
        queryParams =
          (container as GtmIdContainer).queryParams || gtmConfig.queryParams;
      }

      try {
        const scriptUrl = buildScriptUrl(id, queryParams);
        await loadScript(scriptUrl);

        if (isDebug) {
          console.log(`[GTM] Successfully loaded container: ${id}`);
        }
      } catch (error) {
        console.error(`[GTM] Failed to load container ${id}:`, error);

        // Try fallback without preview parameters if they exist
        if (queryParams && (queryParams.gtm_auth || queryParams.gtm_preview)) {
          if (isDebug) {
            console.log(
              `[GTM] Attempting fallback without preview parameters for container: ${id}`,
            );
          }

          try {
            const fallbackUrl = buildScriptUrl(id, undefined);
            await loadScript(fallbackUrl);

            if (isDebug) {
              console.log(`[GTM] Fallback successful for container: ${id}`);
            }
          } catch (fallbackError) {
            console.error(
              `[GTM] Fallback also failed for container ${id}:`,
              fallbackError,
            );

            // Initialize dataLayer even if script fails
            if (import.meta.client && !window.dataLayer) {
              window.dataLayer = [];
              if (isDebug) {
                console.log(
                  `[GTM] Initialized dataLayer manually for container: ${id}`,
                );
              }
            }
          }
        }
      }
    }
  };

  const trackEvent = (params: GtmTrackEventParams) => {
    if (!isEnabled) {
      if (isDebug) {
        console.log("[GTM] Tracking disabled, skipping event:", params);
      }
      return;
    }

    pushToDataLayer(params);
  };

  const trackView = (
    screenName: string,
    path?: string,
    additionalEventData?: Record<string, any>,
  ) => {
    if (!isEnabled) {
      if (isDebug) {
        console.log("[GTM] Tracking disabled, skipping view:", screenName);
      }
      return;
    }

    const eventData = {
      event: gtmConfig.trackViewEventProperty || "content-view",
      "content-name": screenName,
      "content-view-name": screenName,
      ...(path && { "page-path": path }),
      ...additionalEventData,
    };

    pushToDataLayer(eventData);
  };

  const enable = () => {
    isEnabled = true;
    if (isDebug) {
      console.log("[GTM] Tracking enabled");
    }
  };

  const disable = () => {
    isEnabled = false;
    if (isDebug) {
      console.log("[GTM] Tracking disabled");
    }
  };

  const getDataLayer = () => {
    return [...dataLayer];
  };

  const setDebug = (enabled: boolean) => {
    isDebug = Boolean(enabled);
    console.log("[GTM] Debug mode", isDebug ? "enabled" : "disabled");
  };

  const isDebugEnabled = () => isDebug;
  const isTrackingEnabled = () => isEnabled;

  const debugLog = (message: string, data?: any) => {
    if (!isDebug) return;
    if (data !== undefined) {
      console.log(`[GTM][DEBUG] ${message}:`, data);
    } else {
      console.log(`[GTM][DEBUG] ${message}`);
    }
  };

  const gtmInstance: GtmInstance = {
    trackEvent,
    trackView,
    enable,
    disable,
    dataLayer: getDataLayer,
    setDebug,
    isDebugEnabled,
    debugLog,
    isEnabled: isTrackingEnabled,
  };

  await initializeGTM();

  if (gtmConfig.enableRouterSync && import.meta.client) {
    const handleRouteChange = async (to: any, from: any) => {
      if (!isEnabled) return;

      const shouldIgnore =
        typeof gtmConfig.ignoredViews === "function"
          ? gtmConfig.ignoredViews(to, from)
          : gtmConfig.ignoredViews?.includes(to.name);

      if (shouldIgnore) {
        if (isDebug) {
          console.log("[GTM] Ignoring route:", to.name);
        }
        return;
      }

      let additionalEventData = {};
      if (gtmConfig.vueRouterAdditionalEventData) {
        additionalEventData = await gtmConfig.vueRouterAdditionalEventData(
          to,
          from,
        );
      }

      const trackViewAction = () => {
        trackView(to.name || to.path, (keepFilters.value ? to.fullPath : to.path), additionalEventData);
      };

      if (gtmConfig.trackOnNextTick) {
        await nextTick();
        trackViewAction();
      } else {
        trackViewAction();
      }
    };

    router.afterEach(handleRouteChange);
  }

  return nuxtApp.provide("gtm", gtmInstance);
});
